// src/pages/CartPage.jsx
import React from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { getUnitPriceFromTiers, calculateTotalPrice, normalizeAndValidateTiers } from '../utils/moqUtils';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';

const CartPage = () => {
  const { 
    cart, 
    removeFromCart, 
    updateCartQuantity, 
    checkout, 
    openAuthModal,
    user,
    loading
  } = useApp();
  
  
  const navigate = useNavigate();
  
  // MOQ tiers를 정규화하고 총 가격 계산
  const total = cart.reduce((sum, item) => {
    // moqPrices가 존재하고 유효한지 확인
    if (!item.moqPrices || !Array.isArray(item.moqPrices) || item.moqPrices.length === 0) {
      console.error('Invalid moqPrices for item in total calculation:', item);
      return sum;
    }
    
    try {
      // 더미 데이터의 moqPrices 구조를 정규화
      const normalizedTiers = normalizeAndValidateTiers(
        item.moqPrices.map(tier => ({
          minQty: tier.minQty,
          maxQty: tier.maxQty || (tier.minQty + 999), // maxQty가 없으면 임시값 설정
          unitPrice: tier.unitPrice || tier.price // unitPrice가 없으면 price 사용
        }))
      );
      return sum + calculateTotalPrice(normalizedTiers, item.quantity);
    } catch (error) {
      console.error('Error calculating price for item:', item, error);
      return sum; // 에러가 발생하면 0을 반환
    }
  }, 0);

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-light tracking-tight text-gray-900 mb-8">Your Cart</h1>
      {cart.length === 0 ? (
        <div className="text-center py-12">
          <div className="h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v5a2 2 0 01-2 2H9a2 2 0 01-2-2v-5m6-5V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2" />
            </svg>
          </div>
          <p className="text-gray-500 text-lg">Your cart is empty</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 bg-black text-white py-3 px-8 rounded-full hover:bg-gray-800 transition-colors duration-200"
          >
            Browse Products
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {cart.map((item) => {
            // ✅ 임시 항목은 렌더링하지 않음
            if (item.id?.startsWith('temp-')) return null;
            
            // ✅ 아이템이 유효한지 확인
            if (!item || !item.id) {
              console.error('Invalid cart item:', item);
              return null;
            }
            
            // MOQ tiers 정규화 및 안전한 접근
            let normalizedTiers;
            let unitPrice = 0;
            let totalPrice = 0;
            let minQty = 1; // 기본 최소 주문 수량
            
            // moqPrices가 존재하고 유효한지 확인
            if (!item.moqPrices || !Array.isArray(item.moqPrices) || item.moqPrices.length === 0) {
              console.error('Invalid moqPrices for item:', item);
              unitPrice = 0;
              totalPrice = 0;
              minQty = 1;
            } else {
              try {
                normalizedTiers = normalizeAndValidateTiers(
                  item.moqPrices.map(tier => ({
                    minQty: tier.minQty,
                    maxQty: tier.maxQty || (tier.minQty + 999),
                    unitPrice: tier.unitPrice || tier.price
                  }))
                );
                unitPrice = getUnitPriceFromTiers(normalizedTiers, item.quantity);
                totalPrice = calculateTotalPrice(normalizedTiers, item.quantity);
                minQty = normalizedTiers[0]?.minQty || 1;
              } catch (error) {
                console.error('Error processing MOQ tiers for item:', item, error);
                // 기본값 설정 - 안전한 접근
                if (item.moqPrices && item.moqPrices.length > 0) {
                  unitPrice = item.moqPrices[0]?.unitPrice || item.moqPrices[0]?.price || 0;
                  minQty = item.moqPrices[0]?.minQty || 1;
                } else {
                  unitPrice = 0;
                  minQty = 1;
                }
                totalPrice = unitPrice * item.quantity;
              }
            }
            return (
              <div key={item.id} className="flex items-center gap-6 p-6 bg-white rounded-2xl border border-gray-200">
                <img 
                  src={(item.images && item.images.length > 0) ? item.images[0] : '/placeholder-image.svg'} 
                  alt={item.productName || 'Product'}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{item.productName}</h3>
                  <p className="text-gray-600 text-sm">Category: {item.category}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateCartQuantity(item.id, Math.max(minQty, item.quantity - 1))}
                        className="bg-gray-200 rounded-l-full px-2 py-1 hover:bg-gray-300 disabled:opacity-50"
                        disabled={item.quantity <= minQty}
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        min={minQty}
                        value={item.quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val >= minQty) {
                            updateCartQuantity(item.id, val);
                          }
                        }}
                        className="w-16 px-2 py-1 border border-gray-300 text-center text-sm"
                      />
                      <button
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        className="bg-gray-200 rounded-r-full px-2 py-1 hover:bg-gray-300"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <span className="text-gray-600 text-sm">x ${unitPrice.toFixed(2)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">${totalPrice.toFixed(2)}</p>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-700 text-sm mt-2"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="bg-white p-6 rounded-2xl border border-gray-200">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Subtotal</span>
                <span className="text-lg">${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-gray-600">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="flex justify-between items-center text-lg font-medium pt-4 border-t">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
            {!user ? (
              <button
                onClick={openAuthModal}
                className="w-full bg-black text-white py-3 px-4 rounded-full hover:bg-gray-800 transition-colors duration-200 font-medium mt-6"
              >
                Sign In to Checkout
              </button>
            ) : (
              <button
                onClick={checkout}
                disabled={loading}
                className="w-full bg-black text-white py-3 px-4 rounded-full hover:bg-gray-800 transition-colors duration-200 font-medium mt-6 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Proceed to Checkout'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;